import { useCallback, useState } from "react";
import { addAction, addError } from "@jsq/observability";
import {
  useDocDocGoGeneratePresignedUrlsMutation,
  useStartDocumentBatchIngestionMutation,
} from "#graphql/supergraphHooks";
import { FAY_RUM_ACTIONS } from "../constants";
import { resolveUploadContentType } from "../utils/uploadContentType";
import { useRequiredArenaId } from "./useRequiredArenaId";

export type UploadPhase = "idle" | "presigning" | "uploading" | "starting" | "done" | "error";

export type UploadState = {
  phase: UploadPhase;
  /** 0–100; mean across all files. Only meaningful during `uploading`. */
  bytesPercent: number;
  /** Number of files in the in-flight batch. */
  fileCount: number;
  error: string | null;
  ingestionId: string | null;
};

const INITIAL_STATE: UploadState = {
  phase: "idle",
  bytesPercent: 0,
  fileCount: 0,
  error: null,
  ingestionId: null,
};

// XHR (not fetch) so we get upload progress events.
function putWithProgress(
  url: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("S3 upload network error"));
    xhr.send(file);
  });
}

export type UseStartDocumentIngestionReturn = {
  state: UploadState;
  upload: (files: File[]) => Promise<string>;
  reset: () => void;
};

/**
 * @param arenaId Write-path arena id, resolved when the batch is started. Uploads
 * fire on user action, well after the host app's arena resolves, so an absent id
 * here is a programming error that throws rather than silently no-ops.
 */
export function useStartDocumentIngestion(
  arenaId: number | undefined,
): UseStartDocumentIngestionReturn {
  const [state, setState] = useState<UploadState>(INITIAL_STATE);

  const getArenaId = useRequiredArenaId(arenaId);
  const [generatePresignedUrls] = useDocDocGoGeneratePresignedUrlsMutation();
  // Refetch by operation name so the live UploadRail observer is refreshed with
  // its current variables (DocumentIngestions requires arenaId); the object
  // form would refetch with empty variables and drop the required arg.
  const [startIngestion] = useStartDocumentBatchIngestionMutation({
    refetchQueries: ["DocumentIngestions"],
    awaitRefetchQueries: true,
  });

  const upload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        throw new Error("No files to upload");
      }
      setState({
        phase: "presigning",
        bytesPercent: 0,
        fileCount: files.length,
        error: null,
        ingestionId: null,
      });
      // Paired with the result action below: the backend counts only the uploads
      // that arrive, so this is the denominator for an upload completion rate.
      // File names stay out of the context — they are user data and unbounded.
      addAction(FAY_RUM_ACTIONS.uploadStarted, { file_count: files.length });

      try {
        // DDG mints K presigned URLs in one call.
        const presignRes = await generatePresignedUrls({
          variables: {
            files: files.map((file) => ({
              filename: file.name,
              contentType: resolveUploadContentType(file),
              contentLength: file.size,
            })),
          },
        });
        const presigned = presignRes.data?.docDocGoGeneratePresignedUrls;
        if (!presigned || presigned.length !== files.length) {
          throw new Error("Presigned URLs not returned for every file");
        }

        // Match each file to its presigned entry BY FILENAME rather than array
        // position — the response order isn't contractually the request order, and
        // a mismatch would silently upload a file to another file's S3 key. Consume
        // matched entries from a queue so duplicate filenames still pair 1:1.
        const presignedByName = new Map<string, (typeof presigned)[number][]>();
        for (const p of presigned) {
          const bucket = presignedByName.get(p.filename) ?? [];
          bucket.push(p);
          presignedByName.set(p.filename, bucket);
        }
        const matched = files.map((file) => {
          const p = presignedByName.get(file.name)?.shift();
          if (!p) {
            throw new Error(`No presigned URL returned for ${file.name}`);
          }
          return { file, presigned: p };
        });

        // Upload all files to S3 in parallel; bytesPercent is the running mean.
        setState((s) => ({ ...s, phase: "uploading", bytesPercent: 0 }));
        const perFilePct = new Array<number>(matched.length).fill(0);
        await Promise.all(
          matched.map(({ file, presigned: p }, i) =>
            putWithProgress(p.s3Url, file, resolveUploadContentType(file), (pct) => {
              perFilePct[i] = pct;
              const mean = Math.round(
                perFilePct.reduce((a, b) => a + b, 0) / perFilePct.length,
              );
              setState((s) => ({ ...s, bytesPercent: mean }));
            }),
          ),
        );

        // One ingestion workflow for the whole batch.
        setState((s) => ({ ...s, phase: "starting", bytesPercent: 100 }));
        const startRes = await startIngestion({
          variables: {
            input: {
              documents: matched.map(({ presigned: p }) => ({
                objectKey: p.objectKey,
                originalFilename: p.filename,
              })),
              arenaId: getArenaId(),
            },
          },
        });
        const ingestionId = startRes.data?.startDocumentBatchIngestion?.id;
        if (!ingestionId) {
          throw new Error("StartDocumentBatchIngestion returned no id");
        }

        setState({
          phase: "done",
          bytesPercent: 100,
          fileCount: files.length,
          error: null,
          ingestionId,
        });
        // The ingestion id joins this upload to the classification decision that
        // follows it, which can land in a later session. Only the success path
        // carries one: a failed upload starts no ingestion, so there is nothing
        // to join it to.
        addAction(FAY_RUM_ACTIONS.uploadResult, {
          phase: "done",
          file_count: files.length,
          ingestion_id: ingestionId,
        });
        return ingestionId;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed";
        setState({
          phase: "error",
          bytesPercent: 0,
          fileCount: files.length,
          error: message,
          ingestionId: null,
        });
        addAction(FAY_RUM_ACTIONS.uploadResult, {
          phase: "error",
          file_count: files.length,
        });
        // The message goes to error tracking, which is built for unbounded text,
        // and never into an action context.
        addError(err, { fay_operation: "upload" });
        throw err;
      }
    },
    [generatePresignedUrls, startIngestion, getArenaId],
  );

  const reset = useCallback(() => setState(INITIAL_STATE), []);

  return { state, upload, reset };
}
